import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Navigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { Select, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { userPrivilagesAtom } from '../../../atoms/Atoms';
import MainPage from '../main-page/MainPage';
import { H1 } from '../typography/Title';
import { getStores } from '../../../analytics/analyticsApi';

const { RangePicker } = DatePicker;

// Shared shell for the analytics screens: title and one row of filters above
// the content.
//
// Warehouse load status used to sit here as a banner. It now lives on the
// Global-Admin-only Data Warehouse page (pages/data-warehouse), with the run
// history behind it.
//
// The store list is bound to reference/stores rather than hardcoded -- which
// stores go live first is a rollout decision, not a code change.
//
// Every analytics page renders inside this shell, so the role guard lives here:
// the analytics role (hasAnalyticsPrivilages -- GlobalAdmin, Admin or Analytics).
// The API refuses everyone else on its own; this redirect only spares them a
// page of empty charts. It waits until the privileges have been computed, since
// on a hard refresh the atom starts as {} and would bounce a permitted user.
function AnalyticsPage({ title, subtitle, children, onFilterChange }) {
  const { t } = useTranslation();
  const privileges = useRecoilValue(userPrivilagesAtom);
  const privilegesKnown = typeof privileges?.hasAnalyticsPrivilages === 'boolean';
  const canView = privileges?.hasAnalyticsPrivilages === true;
  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [range, setRange] = useState([dayjs('2026-07-01'), dayjs('2026-07-30')]);

  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    getStores()
      .then((d) => { if (alive) setStores(d || []); })
      .catch(() => { if (alive) setStores([]); });
    return () => { alive = false; };
  }, [canView]);

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange({
        businessIds: selectedStores,
        fromDate: range?.[0]?.format('YYYY-MM-DD'),
        toDate: range?.[1]?.format('YYYY-MM-DD'),
      });
    }
  }, [selectedStores, range]);

  if (privilegesKnown && !canView) {
    return <Navigate to="/home" replace />;
  }

  return (
    <MainPage>
      <div className="mt-2">
        <H1 text={title} />
        {subtitle && <p className="mt-1 max-w-3xl text-sm text-muted">{subtitle}</p>}
      </div>

      {/* Filters sit in one row above the charts. */}
      <div className="mb-5 mt-4 flex flex-wrap items-center gap-3">
        <Select
          mode="multiple"
          allowClear
          className="min-w-64"
          style={{ minWidth: '16rem' }}
          placeholder={t('analytics_all_stores')}
          value={selectedStores}
          maxTagCount="responsive"
          onChange={setSelectedStores}
          options={_.map(stores, (s) => ({
            value: s.businessId,
            label: s.businessName,
          }))}
        />
        <RangePicker
          value={range}
          onChange={setRange}
          allowClear={false}
          format="DD MMM YYYY"
        />
        <span className="text-xs text-muted">{t('analytics_dates_are_local')}</span>
      </div>

      {children}
    </MainPage>
  );
}

export default AnalyticsPage;
